import { Divider, Typography } from '@mui/material';
import React, { Fragment, useEffect, useRef } from 'react';

import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import Locale from '../Locale';
import Portlet from '../Portlet';
import PropTypes from 'prop-types';
import ValidationFormFields from './ValidationFormFields.component';
import classNames from 'classnames';
import get from 'lodash/get';
import makeStyles from '@mui/styles/makeStyles';
import styles from './GroupedValidationForm.styles';

const useStyles = makeStyles(styles);

const scrollToRef = ref => ref.current && window.scrollTo(0, ref.current.offsetTop);

/**
 * Renders a schema-driven form as a stack of grouped cards (as opposed to the
 * flat or tabbed layouts). Grouping is declared via the `groups` prop — an
 * ordered list of cards, each with a title/description and an ordered list of
 * field `rows` (schema keys), plus optional `subgroups` rendered as inset panels.
 * Only fields present in `schema` (i.e. the user has permission for) are rendered
 * and empty cards/subgroups are skipped. Form state, validation and submit are
 * still owned by the shared ValidationForm container.
 */
const pickSchema = (schema, names = []) =>
    names.reduce((subSchema, name) => {
        if (schema[name]) {
            subSchema[name] = schema[name];
        }
        return subSchema;
    }, {});

const hasFields = subSchema => Object.keys(subSchema).length > 0;

const GroupedValidationForm = props => {
    const {
        className,
        groups = [],
        schema = {},
        children,
        defaultValues,
        errors,
        disabled,
        isLoading,
        // Optional override for the per-card loading overlay. Lets a consumer
        // (e.g. NotificationForm) keep the skeleton on the initial data load
        // but suppress it while a save is in flight. Falls back to `isLoading`.
        cardLoading = isLoading,
        helperText,
        onSubmit,
        onFieldBlur,
        onFieldChange,
        onFieldRevert,
        canSubmit,
        submitButton,
        submitError,
        title,
        subtitle,
        touched,
        values,
    } = props;
    const classes = useStyles(props);
    const errorRef = useRef(null);

    useEffect(() => {
        submitError && scrollToRef(errorRef);
    }, [submitError]);

    const fieldProps = {
        defaultValues,
        errors,
        disabled,
        isLoading,
        helperText,
        onFieldBlur,
        onFieldChange,
        onFieldRevert,
        touched,
        values,
    };

    // A `type: 'switch'` field renders as a clickable toggle card (bordered,
    // tinted when on) — clicking anywhere in the card toggles it. The switch and
    // its label already toggle themselves, so ignore clicks originating inside
    // the FormControlLabel to avoid a double toggle.
    const renderToggleCard = (field, scheme, nestedSchema = {}) => {
        const isOn = get(values, field, get(defaultValues, field)) === true;

        const toggle = () => {
            if (disabled || isLoading) {
                return;
            }
            onFieldChange(field, !isOn);
        };

        const hasNested = Object.keys(nestedSchema).length > 0;
        const handleRowClick = event => {
            if (event.target.closest('.MuiFormControlLabel-root')) {
                return;
            }
            toggle();
        };

        const handleKeyDown = event => {
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                toggle();
            }
        };

        return (
            <div
                key={field}
                role="button"
                tabIndex={disabled || isLoading ? -1 : 0}
                aria-disabled={disabled || isLoading}
                className={classNames(classes.subgroupRow, isOn && classes.subgroupRowOn)}
                onClick={handleRowClick}
                onKeyDown={handleKeyDown}
            >
                <ValidationFormFields {...fieldProps} schema={{ [field]: scheme }} />
                {hasNested && (
                    // Stop clicks on the nested control (e.g. the score slider)
                    // from bubbling up and toggling the switch.
                    <div
                        className={classes.subgroupRowNested}
                        onClick={event => event.stopPropagation()}
                    >
                        <ValidationFormFields {...fieldProps} schema={nestedSchema} />
                    </div>
                )}
            </div>
        );
    };

    // Mirror ValidationFormFields' hidden/`depends` handling so we can render
    // rows individually (switch → toggle card, others → normal row) and still
    // skip conditionally-hidden fields such as the score threshold slider.
    const shouldRenderField = scheme => {
        if (typeof scheme !== 'object' || scheme === null) {
            return true;
        }
        if (scheme.type === 'hidden') {
            return false;
        }
        if (!scheme.depends) {
            return true;
        }
        return Object.entries(scheme.depends).every(([dependsField, expected]) => {
            const dependsValue = get(values, dependsField, get(defaultValues, dependsField));
            return typeof expected === 'boolean'
                ? Boolean(dependsValue) === expected
                : dependsValue === expected;
        });
    };

    const renderRows = subSchema => {
        const entries = Object.entries(subSchema);
        // A field whose `depends` targets a sibling switch is nested inside that
        // switch's toggle card — so the card's highlight (tinted bg + border)
        // covers it — rather than rendering as its own row. This is what pairs the
        // custom-score slider with the "Use custom privacy score" switch.
        const nestedBySwitch = {};
        const nestedFields = new Set();
        entries.forEach(([field, scheme]) => {
            const dependsKeys = scheme && scheme.depends ? Object.keys(scheme.depends) : [];
            const switchParent = dependsKeys.find(
                dep => subSchema[dep] && subSchema[dep].type === 'switch'
            );
            if (switchParent) {
                (nestedBySwitch[switchParent] = nestedBySwitch[switchParent] || []).push(field);
                nestedFields.add(field);
            }
        });
        const visible = entries.filter(
            ([field, scheme]) => !nestedFields.has(field) && shouldRenderField(scheme)
        );
        return visible.map(([field, scheme], index) => {
            const isSwitch = scheme && scheme.type === 'switch';
            const prevScheme = index > 0 ? visible[index - 1][1] : null;
            const prevIsSwitch = Boolean(prevScheme && prevScheme.type === 'switch');
            // Divider only between two consecutive normal rows — toggle cards use
            // their own border as the separator.
            const showDivider = index > 0 && !isSwitch && !prevIsSwitch;
            const nestedSchema = (nestedBySwitch[field] || [])
                .filter(dep => shouldRenderField(subSchema[dep]))
                .reduce((acc, dep) => {
                    acc[dep] = subSchema[dep];
                    return acc;
                }, {});
            return (
                <Fragment key={field}>
                    {showDivider && <Divider />}
                    {isSwitch ? (
                        renderToggleCard(field, scheme, nestedSchema)
                    ) : (
                        <ValidationFormFields {...fieldProps} schema={{ [field]: scheme }} />
                    )}
                </Fragment>
            );
        });
    };

    return (
        <form
            className={classNames(classes.form, className)}
            onSubmit={onSubmit}
            disabled={disabled}
        >
            {title && (
                <Typography variant="h2">
                    <Locale path={title} />
                </Typography>
            )}
            {subtitle && (
                <Typography className={classes.cardDescription} variant="body1">
                    <Locale path={subtitle} />
                </Typography>
            )}
            {groups.map(group => {
                const mainSchema = pickSchema(schema, group.rows);
                const subgroups = (group.subgroups || [])
                    .map(subgroup => ({
                        ...subgroup,
                        subSchema: pickSchema(schema, subgroup.rows),
                    }))
                    .filter(subgroup => hasFields(subgroup.subSchema));

                if (!hasFields(mainSchema) && subgroups.length === 0) {
                    return null;
                }

                return (
                    <Portlet key={group.id} className={classes.card} loading={cardLoading}>
                        {group.title && (
                            <Typography className={classes.cardTitle} variant="h3">
                                <Locale path={group.title} />
                            </Typography>
                        )}
                        {group.description && (
                            <Typography className={classes.cardDescription} variant="body2">
                                <Locale path={group.description} />
                            </Typography>
                        )}
                        <Divider className={classes.cardDivider} />
                        {hasFields(mainSchema) && (
                            <div className={classes.rows}>{renderRows(mainSchema)}</div>
                        )}
                        {subgroups.map(subgroup => (
                            <div className={classes.subgroup} key={subgroup.id}>
                                <div className={classes.subgroupHeader}>
                                    <BoltOutlinedIcon
                                        className={classes.subgroupIcon}
                                        fontSize="small"
                                    />
                                    {subgroup.title && (
                                        <Typography
                                            className={classes.subgroupLabel}
                                            variant="overline"
                                        >
                                            <Locale path={subgroup.title} />
                                        </Typography>
                                    )}
                                </div>
                                {subgroup.description && (
                                    <Typography
                                        className={classes.subgroupDescription}
                                        variant="body2"
                                    >
                                        <Locale path={subgroup.description} />
                                    </Typography>
                                )}
                                <div className={classes.subgroupRows}>
                                    {Object.entries(subgroup.subSchema).map(([field, scheme]) =>
                                        renderToggleCard(field, scheme)
                                    )}
                                </div>
                            </div>
                        ))}
                    </Portlet>
                );
            })}
            {submitError && (
                <Typography ref={errorRef} className={classes.submitError} variant="body2">
                    <Locale path={submitError} />
                </Typography>
            )}
            {/* Rendered (invisibly) so the container's submit handler is captured
                by consumers via the submitButton render prop. */}
            {submitButton && submitButton({ onClick: onSubmit, disabled: !canSubmit })}
            {children}
        </form>
    );
};

GroupedValidationForm.propTypes = {
    canSubmit: PropTypes.bool,
    cardLoading: PropTypes.bool,
    className: PropTypes.string,
    children: PropTypes.node,
    defaultValues: PropTypes.object,
    disabled: PropTypes.bool,
    errors: PropTypes.object,
    groups: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            title: PropTypes.string,
            description: PropTypes.string,
            rows: PropTypes.arrayOf(PropTypes.string),
            subgroups: PropTypes.arrayOf(
                PropTypes.shape({
                    id: PropTypes.string.isRequired,
                    title: PropTypes.string,
                    description: PropTypes.string,
                    rows: PropTypes.arrayOf(PropTypes.string),
                })
            ),
        })
    ),
    helperText: PropTypes.object,
    isLoading: PropTypes.bool,
    onFieldBlur: PropTypes.func,
    onFieldChange: PropTypes.func,
    onFieldRevert: PropTypes.func,
    onSubmit: PropTypes.func,
    schema: PropTypes.object,
    submitButton: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    submitError: PropTypes.string,
    title: PropTypes.string,
    subtitle: PropTypes.string,
    touched: PropTypes.object,
    values: PropTypes.object,
};

export default GroupedValidationForm;
